import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, Shield, Clock, Users, ArrowRight, Car, FileText, Stethoscope, Scale, AlertTriangle, MapPin, ExternalLink } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const SnohomishCountyCarAccidentPage = () => {
  // FAQ data for schema
  const faqData = [
    {
      question: "What areas in Snohomish County do you serve?",
      answer: "I serve all of Snohomish County including Everett, Marysville, Lake Stevens, Edmonds, Lynnwood, Mountlake Terrace, Mukilteo, Snohomish, Monroe, and surrounding areas. My office is in Everett, but I help clients throughout the county."
    },
    {
      question: "How much does a Snohomish County car accident lawyer cost?",
      answer: "I work on contingency - you pay nothing unless I win your case. No upfront fees, no hourly billing. My fee comes from the settlement, so you can focus on healing without worrying about legal bills."
    },
    {
      question: "Should I accept the insurance company's first offer?",
      answer: "Almost never. First offers are typically far below what your case is worth. Insurance companies count on injured people accepting quick, low settlements. Let me review the offer before you decide."
    },
    {
      question: "How long do I have to file a car accident claim in Snohomish County?",
      answer: "Washington has a 3-year statute of limitations, but waiting is risky. Evidence disappears, witnesses move, and memories fade. Contact me as soon as possible to protect your claim."
    },
    {
      question: "What if the other driver doesn't have insurance?",
      answer: "You may still have options through your own uninsured/underinsured motorist coverage. I'll review all possible sources of compensation and help you understand your options."
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: "Free Consultation",
      description: "Call or text me directly. I'll listen to your story and give you honest advice about your options.",
      icon: Phone
    },
    {
      step: 2,
      title: "I Stop the Insurance Calls",
      description: "Once you hire me, all insurance communication goes through me. No more pressure tactics.",
      icon: Shield
    },
    {
      step: 3,
      title: "You Focus on Healing",
      description: "Get the medical treatment you need. I'll handle the legal paperwork while you recover.",
      icon: Stethoscope
    },
    {
      step: 4,
      title: "I Build Your Case",
      description: "I gather evidence, document injuries, calculate damages, and build the strongest possible claim.",
      icon: FileText
    },
    {
      step: 5,
      title: "You Decide",
      description: "I negotiate maximum compensation. You decide whether to settle or go to trial.",
      icon: Scale
    }
  ];

  const serviceAreas = [
    "Everett", "Marysville", "Lake Stevens", "Edmonds", "Lynnwood", 
    "Mountlake Terrace", "Mukilteo", "Snohomish", "Monroe", "Arlington",
    "Stanwood", "Granite Falls", "Sultan", "Gold Bar", "Index"
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="snohomish-county-car-accident-page">
      <SEOHead 
        title="Snohomish County Car Accident Lawyer | Free Consultation | Amy C. Brown Law"
        description="Injured in a car accident in Snohomish County? I take over the insurance fight so you can focus on healing. Serving Everett, Marysville, Lake Stevens & all of Snohomish County. Call (425) 675-1224."
        faqData={faqData}
        localArea={{ city: "Snohomish County", county: "Snohomish", state: "WA" }}
        serviceData={{
          name: "Snohomish County Car Accident Legal Representation",
          description: "Legal representation for car accident victims throughout Snohomish County, Washington including Everett, Marysville, Lake Stevens, Edmonds, Lynnwood, and surrounding areas.",
          category: "Personal Injury Law - Auto Accidents"
        }}
      />

      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <MapPin className="w-4 h-4" />
              Serving All of Snohomish County
            </div>
            
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6 leading-tight">
              Snohomish County Car Accident Lawyer
            </h1>
            
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              From Everett to Marysville, Lake Stevens to Edmonds—if you've been in a crash anywhere in Snohomish County, I'm here to help. The stress of dealing with insurance companies and medical bills can feel overwhelming.
            </p>

            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all"
                data-testid="hero-text-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl font-bold text-secondary mb-2">
              Serving All Snohomish County Communities
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {serviceAreas.map((area, index) => (
              <span key={index} className="bg-slate-100 text-slate-700 px-4 py-2 rounded-full text-sm">
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              The Problems You're Facing Right Now
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              A car accident creates chaos in your life. I'm here to restore order.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: AlertTriangle, title: "Insurance Pressure", desc: "Adjusters calling constantly, wanting recorded statements, pushing quick settlements." },
              { icon: FileText, title: "Mounting Bills", desc: "Medical bills, car repairs, missed work - the financial stress keeps building." },
              { icon: Clock, title: "Confusing Process", desc: "Forms, deadlines, legal jargon - it's overwhelming when you're in pain." },
              { icon: Car, title: "Unfair Offers", desc: "The insurance company's offer doesn't cover your actual losses." }
            ].map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
                <item.icon className="w-10 h-10 text-primary mb-4" />
                <h3 className="font-heading font-semibold text-secondary mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-xl text-secondary font-medium mb-4">
              Let me take this burden off your shoulders.
            </p>
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center gap-2 text-primary font-semibold hover:underline"
            >
              Talk to me today <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              How Your Case Works
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              A clear process with no surprises. You'll always know what's happening.
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {processSteps.map((step) => (
              <div key={step.step} className="relative">
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 h-full">
                  <div className="bg-primary text-white w-8 h-8 rounded-full flex items-center justify-center font-bold mb-4">
                    {step.step}
                  </div>
                  <step.icon className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-heading font-semibold text-secondary mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm">{step.description}</p>
                </div>
                {step.step < 5 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-slate-300">
                    <ArrowRight className="w-6 h-6" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold text-secondary mb-6">
                Why Snohomish County Chooses Me
              </h2>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">Former Insurance Defense Attorney</h3>
                    <p className="text-slate-600">I spent years on the other side. I know every tactic insurers use to minimize claims - and how to beat them.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">Personal Attention</h3>
                    <p className="text-slate-600">Big firms hand you off to paralegals. With me, you get direct access to your attorney throughout your case.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">Responsive Communication</h3>
                    <p className="text-slate-600">I return calls and texts quickly. You'll never wonder what's happening with your case.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">Local Knowledge</h3>
                    <p className="text-slate-600">Based in Everett, I know Snohomish County roads, courts, and community. That local insight matters.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="font-heading text-2xl font-bold text-secondary mb-6">
                Free Case Evaluation
              </h3>
              <p className="text-slate-600 mb-6">
                Wondering if you have a case? Let's talk. I'll give you an honest assessment - no pressure, no obligation.
              </p>
              <div className="space-y-4">
                <a 
                  href="tel:425-675-1224"
                  className="flex items-center justify-center gap-2 bg-primary text-white px-6 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all w-full"
                >
                  <Phone className="w-5 h-5" />
                  Call (425) 675-1224
                </a>
                <a 
                  href="sms:425-675-1224"
                  className="flex items-center justify-center gap-2 border-2 border-secondary text-secondary px-6 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all w-full"
                >
                  <MessageCircle className="w-5 h-5" />
                  Text Me Now
                </a>
              </div>
              <p className="text-sm text-slate-500 mt-4 text-center">
                Personal response within 1 business day
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              Common Questions About Snohomish County Car Accidents
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-slate-50 p-6 rounded-xl">
                <h3 className="font-heading font-semibold text-secondary mb-3">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Concepts Section - Entity SEO */}
      <section className="py-12 bg-slate-50 border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <h2 className="font-heading text-xl font-semibold text-secondary mb-6 text-center">
            Understanding Car Accident Law
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a href="https://en.wikipedia.org/wiki/Traffic_collision" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Traffic Collision <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Personal_injury" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Personal Injury <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Comparative_negligence" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Comparative Negligence <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Contingent_fee" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Contingency Fee <ExternalLink className="w-3 h-3" />
              </span>
            </a>
          </div>
          <p className="text-xs text-slate-400 text-center mt-4">
            Learn more about these legal concepts on Wikipedia
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-6">
            Injured in Snohomish County? Let Me Fight For You.
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            You focus on recovery. I'll handle the insurance company.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SnohomishCountyCarAccidentPage;
